/* import __COLOCATED_TEMPLATE__ from './project-details-pitch.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { showModal } from 'sh-www/utils/modal-controls';
import { task } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import {
  PROJECT_DETAILS__APPLICATION_EDITABLE_PITCH_STATES_SELLER,
} from 'sh-www/constants/routes/project-details';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-project-details-pitch';

const createLink = (link, id) => ({
	guid: crypto.randomUUID(),
	link,
	id,
});

class ProjectDetailsPitch extends Component {
  @service('state-managers/application') applicationStateManager;
  @service('commands/image-commands') imageCommandService;
  @service('commands/location-commands') locationCommandService;
  @service('commands/pitch-commands') pitchCommandService;
  @service('error-tracker') errorTracker;
  @service session;

  @tracked tempSelectedLocationName = '';
  @tracked isEditingStory = false;
  @tracked isEditingPreviouslyProduced = false;
  @tracked isEditingWebUrls = false;
  @tracked isEditingCharacters = false;
  @tracked isEditingLocation = false;
  @tracked updatedPitchLocation = null;
  @tracked updatedIsProduced = false;
  @tracked updatedWebUrls = [];
  @tracked updatedProductionLink = '';

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  get isCanEditPitch() {
    return !this.session.currentUser.isBuyer && PROJECT_DETAILS__APPLICATION_EDITABLE_PITCH_STATES_SELLER.includes(this.args.application.status);
  }

  get pitchLocationText() {
    const location = this.args.application.pitch?.location;
    if (!location?.formattedAddress) {
      return 'Not selected';
    }

    return location.formattedAddress;
  }

  get updatedCityLocationText() {
    return this.tempSelectedLocationName || this.args.application.pitch?.location?.formattedAddress || '';
  }

  get originalPitchLocation() {
    return this.args.application.pitch?.location ?? null;
  }

  get pitchImageHeightStyle() {
    return htmlSafe(`max-height: ${this.args.application.pitch.image.height}px`);
  }

  @action
  onWeburlLinkTextChange(linkText, index) {
    const webUrls = [...this.updatedWebUrls];
    webUrls[index] = { ...webUrls[index], link: linkText };
    const lastLink = webUrls.at(-1);

    if (lastLink?.link) {
      webUrls.push(createLink(''));
    }

    this.updatedWebUrls = webUrls;
  }

  @action
  toggleIsEditingCharacters(toggleBool) {
    this.isEditingCharacters = toggleBool;
  }

  @action
  onSaveCharactersClicked(characters) {
    this.saveCharactersTask.perform(characters);
  }

  @task
  *saveCharactersTask(characters) {
    try {
      this.isEditingCharacters = false;
      yield this.updatePitch.perform({ characters });
    } catch (e) {
      this.isEditingCharacters = true;
    }
  }

  @action
  toggleIsEditingLocation(toggleBool) {
    if (!toggleBool) {
      // revert to original location model
      this.updatedPitchLocation = this.originalPitchLocation;
      this.isEditingLocation = false;
    } else {
      this.updatedPitchLocation = null;
      this.isEditingLocation = true;
    }
  }

  @action
  onSaveLocationClicked() {
    this.saveLocationTask.perform();
  }

  @task
  *saveLocationTask() {
    this.isEditingLocation = false;
    yield this.updatePitch.perform({ 'location_id' : this.updatedPitchLocation.id });
  }

  @task
  *setLocationFromGoogleRecord(locationRecord) {
    if (!locationRecord?.place_id) {
      // we didn't get a proper place from google.  Can happen when clicking enter on existing place or clear field
      this.updatedPitchLocation = null;
      this.tempSelectedLocationName = '';
      return;
    }

    // use the raw data to set a temp label to the location
    this.tempSelectedLocationName = locationRecord?.formatted_address ?? '';

    try {
      const location = yield this.locationCommandService.createLocationFromLocationRecord(locationRecord);
      this.updatedPitchLocation = location;
    } catch (e) {
      this.updatedPitchLocation = null;
    }
  }

  @action
  toggleEditingPreviouslyProduced(isEditingPreviouslyProduced) {
    if (isEditingPreviouslyProduced) {
      const { isProduced, productionLink } = this.args.application.pitch;
      this.updatedIsProduced = isProduced;
      this.updatedProductionLink = productionLink;
    }

    this.isEditingPreviouslyProduced = isEditingPreviouslyProduced;
  }

  @action
  onRadioIsProducedClicked(isProduced) {
    this.updatedIsProduced = isProduced;
  }

  @action
  onSavePreviouslyProducedClicked() {
    this.savePreviouslyProducedTask.perform();
  }

  @task
  *savePreviouslyProducedTask() {
    this.isEditingPreviouslyProduced = false;

    try {
      yield this.updatePitch.perform({
        'produced' : this.updatedIsProduced,
        'production_link' : this.updatedProductionLink
      });
    }
    catch (e) {
      this.isEditingPreviouslyProduced = true;
    }
  }

  @action
  toggleEditingStory(toggleBool) {
    this.isEditingStory = toggleBool;
  }

  @action
  onSaveStoryClicked(newStory) {
    this.updateStoryTask.perform(newStory);
  }

  @task
  *updateStoryTask(updatedStory) {
    this.isEditingStory = false;
    try {
      yield this.updatePitch.perform({'summary' : updatedStory});
    } catch (e) {
      this.isEditingStory = true;
    }
  }

  @action
  toggleEditingWebUrls(isEditingWebUrls) {
    const webUrls = isEditingWebUrls
      ? (this.args.application.pitch?.weburls ?? []).map(({id, link}) => createLink(link, id))
      : [];
    webUrls.push(createLink(''));

    this.updatedWebUrls = webUrls;
    this.isEditingWebUrls = isEditingWebUrls;
  }

  @action
  onSaveWebUrlsClicked() {
    this.updateWebUrlsTask.perform();
  }

  @task
  *updateWebUrlsTask() {
    const linksToSave = this.updatedWebUrls.reduce((accum, {link}) => {
      if (link) {
        accum.push(link);
      }
      return accum;
    }, []);
    this.isEditingWebUrls = false;
    try {
      yield this.updatePitch.perform({ 'links' : linksToSave });
    } catch (e) {
      this.isEditingWebUrls = true;
    }
  }

  @action
  handleProductionLinkChange(text) {
    this.updatedProductionLink = text;
  }

  @task
  *updatePitch(propsToUpdate) {
    const { application } = this.args;
    try {
      yield this.pitchCommandService.updatePitch(
        application.pitch,
        propsToUpdate,
        ['location','weburls','image']
      );
      this.applicationStateManager.addToast('Pitch updated.');
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem updating pitch. Please try again');
      this.errorTracker.error(e, this, `updatePitch failed - application id ${application.id} || pitch id ${application.pitch.id}`);
      throw e;
    }
  }

  @action
  deleteUpdateWebUrl(webUrl) {
    this.updatedWebUrls = this.updatedWebUrls.filter(currentWebUrl => {
      return currentWebUrl !== webUrl;
    });

    if (!this.updatedWebUrls.length) {
      this.updatedWebUrls = [{ link: '' }];
    }
  }

  @action
  onDeleteImageClicked() {
    const modalData = {
      titleText: 'Delete this image?',
      bodyText: `Are you sure you want to remove this image from this pitch?`,
      actionButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      isShowCancelButton: true,
      confirmAction: () => {
        this.deletePitchImage.perform(this.args.application);
      }
    };
    showModal(this, 'confirmation', modalData);
  }

  @task
  *deletePitchImage(application) {
    yield this.pitchCommandService.deletePitchImage(application.pitch);
    yield this.args.loadApplicationTask.unlinked().perform(application.id, ['pitch.image']);
  }
}

export default ProjectDetailsPitch;
